import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoogleSignIn from './GoogleSignIn';
import InterestSelection from './InterestSelection';

export const Register = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: searchParams.get('inviteCode') || ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showInterestSelection, setShowInterestSelection] = useState(false);
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  // Detect if running in PWA standalone mode
  const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                window.navigator.standalone === true;

  const handleGoogleSignIn = async (response) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await googleLogin(response.credential);
      
      if (result.success) {
        navigate('/');
      } else {
        setError(result.message || 'Google registration failed');
      }
    } catch (err) {
      setError('An error occurred during Google registration');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (err) => {
    setError('Google Sign-In failed');
    console.error(err);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user changes input
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.displayName || !formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await register({
        displayName: formData.displayName,
        email: formData.email,
        password: formData.password,
        inviteCode: formData.inviteCode
      });
      
      if (result.success) {
        setShowInterestSelection(true);
      } else {
        setError(result.message || 'Registration failed');
      }
    } catch (err) {
      setError('An error occurred during registration');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tennis-court min-h-screen flex items-center justify-center">
      <div className="max-w-md w-full px-4">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-center text-brown-800 mb-6">
            Register for SBWOM
          </h2>

          {isPWA && (
            <div className="bg-blue-100 border border-blue-400 text-blue-800 px-4 py-3 rounded mb-4 text-sm">
              <div className="flex items-start">
                <span className="text-lg mr-2">💡</span>
                <div>
                  <strong>Using the App?</strong>
                  <p className="mt-1">For the best experience in the standalone app, we recommend using <strong>Face ID/Touch ID</strong> (Passkey) to sign up. Google Sign-In may not work reliably in app mode due to security restrictions.</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="displayName" className="block text-brown-700 text-sm font-bold mb-2">
                Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-brown-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="displayName"
                  name="displayName"
                  className="shadow appearance-none border rounded-lg w-full py-3 pl-10 pr-4 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 transition-colors duration-300"
                  value={formData.displayName}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  required
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="email" className="block text-brown-700 text-sm font-bold mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-brown-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="shadow appearance-none border rounded-lg w-full py-3 pl-10 pr-4 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 transition-colors duration-300"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="password" className="block text-brown-700 text-sm font-bold mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-brown-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  className="shadow appearance-none border rounded-lg w-full py-3 pl-10 pr-4 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 transition-colors duration-300"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="confirmPassword" className="block text-brown-700 text-sm font-bold mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-brown-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  className="shadow appearance-none border rounded-lg w-full py-3 pl-10 pr-4 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 transition-colors duration-300"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="inviteCode" className="block text-brown-700 text-sm font-bold mb-2">
                Invite Code (Optional)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-brown-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="inviteCode"
                  name="inviteCode"
                  className="shadow appearance-none border rounded-lg w-full py-3 pl-10 pr-4 text-brown-700 leading-tight focus:outline-none focus:shadow-outline focus:border-green-500 transition-colors duration-300"
                  value={formData.inviteCode}
                  onChange={handleChange}
                  placeholder="Enter your invite code"
                />
              </div>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center">
                <input
                  id="agree-terms"
                  name="agree-terms"
                  type="checkbox"
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-brown-300 rounded"
                  required
                />
                <label htmlFor="agree-terms" className="ml-2 block text-sm text-brown-700">
                  I agree to the{' '}
                  <Link to="/terms" className="text-green-600 hover:text-green-500 font-medium">
                    Terms and Conditions
                  </Link>
                </label>
              </div>
            </div>
            
            <div className="mb-6">
              <button
                type="submit"
                className={`w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition-colors duration-300 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isLoading}
              >
                {isLoading ? 'Registering...' : 'Register'}
              </button>
            </div>
          </form>
          
          <div className="mb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <Link
                to={`/passkey-register${formData.inviteCode ? `?inviteCode=${formData.inviteCode}` : ''}`}
                className="w-full bg-brown-600 hover:bg-brown-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors duration-300"
              >
                <span className="text-xl mr-2">🔐</span>
                Sign up with Face ID/Touch ID
              </Link>

              <div className="flex justify-center">
                <GoogleSignIn
                  onSuccess={handleGoogleSignIn}
                  onError={handleGoogleError}
                  buttonText="Sign up with Google"
                />
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-brown-600">
              Already have an account?{' '}
              <Link to="/login" className="text-green-600 hover:text-green-500 font-medium">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>

      {showInterestSelection && (
        <InterestSelection
          onComplete={() => navigate('/')}
          onSkip={() => navigate('/')}
        />
      )}
    </div>
  );
};

export default Register;